import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Editor } from '@tinymce/tinymce-react'
import DOMPurify from 'dompurify'
import { useAuth } from '../../context/AuthContext'
import * as clubsApi from '../../api/clubs'
import type { ClubDetail } from '../../types'
import { EventManager } from '../../components/clubs/EventManager'

export function ClubEditor() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const editorRef = useRef<{ getContent: () => string } | null>(null)

  const [detail, setDetail] = useState<ClubDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [previewing, setPreviewing] = useState(false)

  // Form state
  const [description, setDescription] = useState('')
  const [meetingInfo, setMeetingInfo] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [facebook, setFacebook] = useState('')
  const [instagram, setInstagram] = useState('')
  const [youtube, setYoutube] = useState('')
  const [customContent, setCustomContent] = useState('')

  useEffect(() => {
    if (!slug) return
    async function load() {
      try {
        const data = await clubsApi.getClub(slug!)
        setDetail(data)

        // Check if user is a secretary
        const isSecretary = data.officers.some(
          (o) => o.role.includes('Secretary') && o.auid === user?.auid
        )
        if (!isSecretary) {
          setError('You must be a club secretary to edit this page.')
          setLoading(false)
          return
        }

        // Pre-fill form with existing page data
        if (data.page) {
          setDescription(data.page.description || '')
          setMeetingInfo(data.page.meetingInfo || '')
          setContactEmail(data.page.contactEmail || '')
          setContactPhone(data.page.contactPhone || '')
          setWebsite(data.page.website || '')
          setCustomContent(data.page.customContent || '')
          const social = data.page.socialMedia || {}
          setFacebook(social.facebook || '')
          setInstagram(social.instagram || '')
          setYoutube(social.youtube || '')
        }
      } catch {
        setError('Failed to load club data.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slug, user])

  const handleSave = useCallback(async () => {
    if (!detail) return
    setSaving(true)
    setError('')
    setSuccess('')

    const content = editorRef.current?.getContent() ?? customContent
    const socialMedia: Record<string, string> = {}
    if (facebook) socialMedia.facebook = facebook
    if (instagram) socialMedia.instagram = instagram
    if (youtube) socialMedia.youtube = youtube

    try {
      const result = await clubsApi.saveClubPage(detail.club.clubNum, {
        description,
        meetingInfo,
        contactEmail,
        contactPhone,
        website,
        socialMedia,
        customContent: content,
      })
      setSuccess('Club page saved successfully! Last updated: ' + new Date(result.lastUpdated).toLocaleString())
      setCustomContent(content)
    } catch {
      setError('Failed to save club page. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [detail, description, meetingInfo, contactEmail, contactPhone, website, facebook, instagram, youtube, customContent])

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!detail || !e.target.files?.[0]) return
    const file = e.target.files[0]
    setError('')

    try {
      const result = await clubsApi.uploadClubImage(detail.club.clubNum, file)
      setSuccess('Photo uploaded: ' + result.photoUrl)
    } catch {
      setError('Failed to upload image. Max size is 5MB.')
    }
  }, [detail])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
        <span className="ml-3 text-text-muted">Loading editor...</span>
      </div>
    )
  }

  if (error && !detail) {
    return (
      <div className="text-center py-16">
        <p className="text-error mb-4">{error}</p>
        <Link to={`/clubs/${slug}`} className="text-primary hover:underline">Back to club page</Link>
      </div>
    )
  }

  if (!detail) return null

  const previewHtml = editorRef.current?.getContent() ?? customContent

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to={`/clubs/${slug}`} className="text-sm text-primary hover:underline mb-2 inline-block">
            &larr; Back to {detail.club.clubName}
          </Link>
          <h1 className="text-2xl font-heading font-bold text-text">Edit Club Page</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPreviewing(!previewing)}
            className="px-4 py-2 border border-border rounded-md text-text hover:bg-surface-hover transition-colors text-sm"
          >
            {previewing ? 'Edit' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/30 text-success rounded-md px-4 py-3 mb-4 text-sm">
          {success}
        </div>
      )}

      {previewing ? (
        <div className="border border-border rounded-lg p-6 bg-surface">
          <h2 className="font-heading font-semibold text-text mb-4">Preview</h2>
          {previewHtml ? (
            <div
              className="prose prose-sm max-w-none text-text"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
            />
          ) : (
            <p className="text-text-muted">No custom content yet.</p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Structured fields */}
          <div className="border border-border rounded-lg p-6 bg-surface">
            <h2 className="font-heading font-semibold text-text mb-4">Club Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Brief description of your club..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Meeting Info</label>
                <textarea
                  value={meetingInfo}
                  onChange={(e) => setMeetingInfo(e.target.value)}
                  rows={2}
                  placeholder="e.g. 2nd Tuesday of each month, 7pm at..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Website</label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Social media */}
          <div className="border border-border rounded-lg p-6 bg-surface">
            <h2 className="font-heading font-semibold text-text mb-4">Social Media</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Facebook</label>
                <input
                  type="url"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Instagram</label>
                <input
                  type="url"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">YouTube</label>
                <input
                  type="url"
                  value={youtube}
                  onChange={(e) => setYoutube(e.target.value)}
                  placeholder="https://youtube.com/..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Photo upload */}
          <div className="border border-border rounded-lg p-6 bg-surface">
            <h2 className="font-heading font-semibold text-text mb-4">Club Photo</h2>
            {detail.page?.photoUrl && (
              <img
                src={detail.page.photoUrl}
                alt="Current club photo"
                className="w-full max-h-48 object-cover rounded-md mb-4"
              />
            )}
            <label className="block">
              <span className="text-sm text-text-muted">Upload a new photo (max 5MB, JPEG/PNG/WebP)</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                className="mt-2 block w-full text-sm text-text file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-border file:text-sm file:font-medium file:bg-surface file:text-text hover:file:bg-surface-hover"
              />
            </label>
          </div>

          {/* Rich text editor */}
          <div className="border border-border rounded-lg p-6 bg-surface">
            <h2 className="font-heading font-semibold text-text mb-4">Custom Content</h2>
            <p className="text-sm text-text-muted mb-3">
              Use the rich text editor to add custom content to your club page. This will appear prominently on your club's public page.
            </p>
            <Editor
              tinymceScriptSrc="/tinymce/tinymce.min.js"
              licenseKey="gpl"
              onInit={(_evt, editor) => {
                editorRef.current = editor
              }}
              initialValue={customContent}
              init={{
                height: 400,
                menubar: true,
                plugins: [
                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                  'anchor', 'searchreplace', 'visualblocks', 'code',
                  'insertdatetime', 'media', 'table', 'help', 'wordcount',
                ],
                toolbar:
                  'undo redo | blocks | bold italic forecolor | ' +
                  'alignleft aligncenter alignright alignjustify | ' +
                  'bullist numlist outdent indent | link image | removeformat | help',
                content_style: 'body { font-family: Inter, system-ui, sans-serif; font-size: 14px; }',
              }}
            />
          </div>

          {/* Save button */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(`/clubs/${slug}`)}
              className="px-4 py-2 border border-border rounded-md text-text hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Club Page'}
            </button>
          </div>

          {/* Events & Announcements — saved independently of the page content above */}
          <EventManager clubNum={detail.club.clubNum} initialEvents={detail.events ?? []} />
        </div>
      )}
    </div>
  )
}
